// WHAT: Main app component for Miracle Flow with router-based navigation
// WHY:  Provides layout and tab navigation for Miracle Flow application
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: React Router, Card, Button, Stack, Heading components
// RISKS:
//   - NOTE: Base route detection may not handle all routing scenarios
//   - NOTE: Tab configuration is hardcoded - may need dynamic updates

import { Sun, Moon, History, BarChart3, Heart, Sparkles } from "lucide-react";
import React from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";

import { Card, Button, Stack, Heading } from "@/components/ui";

/**
 * WHAT:
 *   Main app component that provides layout and tab navigation for Miracle Flow
 *   application with React Router integration.
 *
 * WHY:
 *   Follows consistent pattern with other apps (Roadmap, TimeTracQ) and provides
 *   unified navigation experience.
 *
 * BEHAVIOR:
 *   - Detects base route (supports /miracle-flow and /nexus/personal-development/miracle-flow)
 *   - Configures tabs with dynamic paths
 *   - Highlights current tab based on pathname
 *   - Renders tab content via Outlet
 *   - Provides header with branding
 *
 * RISKS:
 *   - NOTE: Base route detection may not handle all routing scenarios
 *   - NOTE: Tab configuration is hardcoded - may need dynamic updates
 *
 * @returns MiracleFlowApp component
 *
 */
export const MiracleFlowApp: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * WHAT:
   *   Extracts base route from current pathname.
   *
   * WHY:
   *   Supports multiple routing scenarios (direct and nested routes).
   *
   * BEHAVIOR:
   *   - Checks for nested route first
   *   - Falls back to direct route
   *
   * @returns Base route string
   *
   */
  const getBaseRoute = () => {
    const pathname = location.pathname;
    if (pathname.startsWith("/nexus/personal-development/miracle-flow")) {
      return "/nexus/personal-development/miracle-flow";
    }
    return "/miracle-flow";
  };

  const baseRoute = getBaseRoute();

  // NOTE: Tab configuration with dynamic paths
  const tabs = [
    {
      description: "Your daily routine overview",
      icon: Sparkles,
      id: "dashboard",
      label: "Dashboard",
      path: baseRoute,
    },
    {
      description: "Start your day with intention",
      icon: Sun,
      id: "morning-session",
      label: "Morning Session",
      path: `${baseRoute}/morning-session`,
    },
    {
      description: "Reflect and prepare for tomorrow",
      icon: Moon,
      id: "evening-session",
      label: "Evening Session",
      path: `${baseRoute}/evening-session`,
    },
    {
      description: "View past sessions and progress",
      icon: History,
      id: "session-history",
      label: "Session History",
      path: `${baseRoute}/session-history`,
    },
    {
      description: "Manage your affirmations",
      icon: Heart,
      id: "affirmation-settings",
      label: "Affirmation Settings",
      path: `${baseRoute}/affirmation-settings`,
    },
    {
      description: "Detailed analytics and insights",
      icon: BarChart3,
      id: "stats-overview",
      label: "Statistics",
      path: `${baseRoute}/stats-overview`,
    },
  ];

  // NOTE: Find current tab by matching pathname or checking if it's the base route
  const currentTab =
    tabs.find((tab) => {
      if (tab.id === "dashboard") {
        return location.pathname === baseRoute || location.pathname === `${baseRoute}/`;
      }
      return location.pathname === tab.path;
    }) ?? tabs[0];

  const handleTabClick = (path: string) => {
    const result = navigate(path);
    if (result instanceof Promise) {
      result.catch(() => {
        // Navigation errors are handled silently
      });
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="w-full max-w-none px-6 py-6">
        <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={4}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h1" size="xl">
                  Miracle Flow
                </Heading>
                <p className="text-[#94A3B8]">Your daily routine for success and personal growth</p>
              </Stack>
            </Stack>
            <div className="flex items-center gap-2">
              <Button
                className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
                onClick={() => {
                  const result = navigate("/dashboard");
                  if (result instanceof Promise) {
                    result.catch(() => {
                      // Navigation errors are handled silently
                    });
                  }
                }}
                size="sm"
                variant="outline"
              >
                Back to Dashboard
              </Button>
            </div>
          </Stack>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="w-full max-w-none px-6">
        <Card className="bg-[#1E293B] border-[#334155]" variant="default">
          <div className="border-b border-[#334155]">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                      currentTab?.id === tab.id
                        ? "border-tracq-bright-blue text-tracq-bright-blue"
                        : "border-transparent text-[#94A3B8] hover:text-[#CBD5E1] hover:border-[#475569]"
                    }`}
                    key={tab.id}
                    onClick={() => {
                      handleTabClick(tab.path);
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </Card>
      </div>

      {/* Tab Content */}
      <div className="w-full max-w-none px-6 py-6">
        <div className="space-y-6">
          <Outlet key={location.pathname} />
        </div>
      </div>
    </div>
  );
};
